import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  BadgeIndianRupee,
  BookOpen,
  Boxes,
  Briefcase,
  Building2,
  CalendarCheck,
  ChartLine,
  ClipboardCheck,
  FolderTree,
  GitBranch,
  Layers,
  LayoutDashboard,
  LogOut,
  MapPin,
  Route,
  Menu,
  Monitor,
  Moon,
  Package,
  Percent,
  Receipt,
  Scale,
  ScanBarcode,
  ShoppingBag,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Sun,
  Target,
  User,
  UserCheck,
  UserPlus,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { apiUrl } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { ROUTE_ACCESS } from '@/features/auth/access'
import { useAuth } from '@/features/auth/store'
import { getCompanySettings } from '@/features/company/api'
import { cn } from '@/lib/cn'
import { useTheme } from '@/lib/theme'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
}

interface NavSection {
  /** Shown above the group on the desktop rail and in the phone drawer. */
  label: string
  items: NavItem[]
}

/**
 * Grouped rather than one long list.
 *
 * What each item needs is not written here: `useVisibleNav` looks the destination up in
 * `ROUTE_ACCESS`, the same table the router guards with. A link and the screen behind it
 * therefore cannot disagree about who may open it.
 *
 * The groups are what a user is doing, not what the API calls things: the money screens sit
 * together, the things only an admin touches sit apart from them. A section whose every
 * item is hidden by permission renders nothing at all, so a field rep does not see an empty
 * "Administration" heading.
 */
const NAV: NavSection[] = [
  {
    label: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Insights',
    items: [
      { to: '/analytics/sales', label: 'Sales analytics', icon: ChartLine },
      { to: '/analytics/products', label: 'Products', icon: ShoppingBag },
      { to: '/analytics/customers', label: 'Customers', icon: Target },
      { to: '/analytics/inventory', label: 'Inventory', icon: Boxes },
    ],
  },
  {
    label: 'Trade',
    items: [
      { to: '/orders', label: 'Sales orders', icon: ShoppingCart },
      { to: '/invoices', label: 'Invoices', icon: Receipt },
      { to: '/customers', label: 'Customers', icon: Target },
      { to: '/leads', label: 'Nearby leads', icon: MapPin },
      { to: '/field-routes', label: 'Field routes', icon: Route },
    ],
  },
  {
    label: 'Counter',
    items: [
      { to: '/pos', label: 'Point of sale', icon: ScanBarcode },
      { to: '/pos/shift', label: 'Shift', icon: Wallet },
      { to: '/pos/shifts', label: 'Shifts report', icon: Users },
      { to: '/pos/terminals', label: 'Tills', icon: Monitor },
    ],
  },
  {
    label: 'Stock',
    items: [
      { to: '/products', label: 'Products', icon: Package },
      { to: '/categories', label: 'Categories', icon: FolderTree },
      { to: '/tax-rules', label: 'Tax rules', icon: Receipt },
      { to: '/stock/valuation', label: 'Stock valuation', icon: Boxes },
      {
        to: '/stock/adjustments',
        label: 'Corrections',
        icon: ClipboardCheck,
      },
      { to: '/stock/locations', label: 'Locations', icon: MapPin },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/accounting/journal', label: 'Journal', icon: BookOpen },
      {
        to: '/accounting/trial-balance',
        label: 'Trial balance',
        icon: Scale,
      },
      {
        to: '/accounting/accounts',
        label: 'Chart of accounts',
        icon: Wallet,
      },
      {
        to: '/commissions/statement',
        label: 'Commissions',
        icon: Percent,
      },
      {
        to: '/commissions/payroll',
        label: 'Payroll preview',
        icon: BadgeIndianRupee,
      },
      {
        to: '/commissions/rules',
        label: 'Rate cards',
        icon: SlidersHorizontal,
      },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/employees', label: 'Employees', icon: Users },
      {
        to: '/hr/reviews',
        label: 'Performance reviews',
        icon: UserCheck,
      },
      { to: '/hr/attendance', label: 'Attendance', icon: CalendarCheck },
      { to: '/recruitment/postings', label: 'Job postings', icon: Briefcase },
      { to: '/recruitment/candidates', label: 'Candidates', icon: UserPlus },
      { to: '/recruitment/applications', label: 'Applications', icon: GitBranch },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/users', label: 'Users', icon: Users },
      { to: '/roles', label: 'Roles & permissions', icon: ShieldCheck },
      {
        to: '/settings/company',
        label: 'Company settings',
        icon: Building2,
      },
      { to: '/status', label: 'System status', icon: Activity },
    ],
  },
]

/** The nav items this user may actually open, empty sections dropped. */
function useVisibleNav(): NavSection[] {
  const can = useAuth((s) => s.can)
  const allowed = (item: NavItem) => {
    const need = ROUTE_ACCESS[item.to]
    return !need || can(need.resource, need.action)
  }
  return NAV.map((section) => ({
    ...section,
    items: section.items.filter(allowed),
  })).filter((section) => section.items.length > 0)
}

const LINK_BASE =
  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors'

const LINK_IDLE =
  'text-slate-600 hover:bg-slate-100 hover:text-slate-900 ' +
  'dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'

const LINK_ACTIVE =
  'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 font-semibold border-r-2 border-emerald-500 shadow-sm'

/** The grouped destinations: the rail down the left edge on desktop, the drawer below it. */
function SidebarNav() {
  const sections = useVisibleNav()

  return (
    <nav aria-label="Main" className="space-y-6">
      {sections.map((section) => (
        <div key={section.label} className="space-y-1">
          <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {section.label}
          </p>
          {section.items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => cn(LINK_BASE, isActive ? LINK_ACTIVE : LINK_IDLE)}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  )
}

/**
 * The rail as a slide-in drawer, below the desktop breakpoint, opened from the header's
 * menu button. There are too many destinations for a bottom bar: it ran off the side of a
 * phone and scrolled sideways.
 *
 * Closes on any navigation (so tapping a link both goes there and gets out of the way), on
 * Escape and on a tap outside. The page behind does not scroll while it is open.
 */
function NavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation()

  useEffect(() => {
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a route change closes it
  }, [pathname])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = overflow
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-40 desktop:hidden" role="dialog" aria-modal="true" aria-label="Menu">
      <div className="absolute inset-0 bg-slate-950/50" onClick={onClose} aria-hidden="true" />
      <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex h-16 items-center justify-between gap-2 border-b border-slate-200 px-4 dark:border-slate-800">
          <Brand />
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close menu">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
          <SidebarNav />
        </div>
      </aside>
    </div>
  )
}

/**
 * The company's own logo where the product mark would otherwise sit.
 *
 * The read is guarded by `invoices` -- most roles hold it, a couple do not -- so the query
 * only runs when the user may make it. Firing a 403 on every page load to decide what to
 * draw in a header is not a trade worth making, and the fallback is the same either way.
 *
 * Three things fall back to the product mark: no logo uploaded, no permission to look, and
 * a signed URL that went stale while the tab sat open. The last one is why `onError` is
 * here at all -- the download URL is short-lived by design, and a broken image icon in the
 * corner of every screen would be worse than no logo.
 */
function useBrand() {
  const canAccess = useAuth((s) => s.canAccess)
  const query = useQuery({
    queryKey: ['company-settings'],
    queryFn: getCompanySettings,
    enabled: canAccess('invoices'),
    retry: false,
  })
  return query.data ?? null
}

function Brand({ compact = false }: { compact?: boolean }) {
  const settings = useBrand()
  const [broken, setBroken] = useState(false)
  const name = settings?.trade_name || settings?.legal_name

  if (settings?.logo_url && !broken) {
    return (
      <img
        src={apiUrl(settings.logo_url)}
        alt={name || 'FMCG ERP Suite'}
        className="h-10 w-auto max-w-full object-contain object-left"
        onError={() => setBroken(true)}
      />
    )
  }

  if (compact) {
    return (
      <Link to="/dashboard" className="flex items-center gap-2 transition-opacity hover:opacity-90">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-cyan-500 shadow-md shadow-emerald-500/20">
          <Layers className="h-4 w-4 text-white" />
        </div>
        <span className="truncate text-sm font-bold tracking-tight text-slate-900 dark:text-white">
          FMCG <span className="text-emerald-600 dark:text-emerald-400">ERP</span>
        </span>
      </Link>
    )
  }

  return (
    <Link to="/dashboard" className="flex items-center gap-2.5 transition-opacity hover:opacity-90">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-emerald-600 to-cyan-500 shadow-md shadow-emerald-500/20">
        <Layers className="h-4.5 w-4.5 text-white" />
      </div>
      <div className="flex flex-col leading-none">
        <span className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">
          FMCG <span className="text-emerald-600 dark:text-emerald-400">ERP Suite</span>
        </span>
        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400 mt-1">
          AI Enterprise
        </span>
      </div>
    </Link>
  )
}

function UserMenu() {
  const user = useAuth((s) => s.user)
  const signOut = useAuth((s) => s.signOut)
  const navigate = useNavigate()
  const [signingOut, setSigningOut] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  if (!user) return null

  const handleSignOut = async () => {
    setSigningOut(true)
    await signOut()
    navigate('/login', { replace: true })
  }

  const initials = user.full_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="group flex items-center justify-center rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-100 to-brand-200 text-sm font-bold text-brand-700 shadow-sm transition-transform group-hover:scale-105 group-active:scale-95 dark:from-brand-900/60 dark:to-brand-800/60 dark:text-brand-300">
            {initials}
          </div>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-64 origin-top-right rounded-xl border border-slate-200 bg-white/95 p-1.5 shadow-xl backdrop-blur-xl ring-1 ring-black/5 animate-in fade-in zoom-in-95 focus:outline-none dark:border-slate-800 dark:bg-slate-950/95">
            <div className="px-3 py-3">
              <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                {user.full_name}
              </p>
              <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                {user.roles.map((r) => r.name).join(', ') || 'No role assigned'}
              </p>
            </div>
            <div className="my-1 h-px bg-slate-200 dark:bg-slate-800" />
            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/profile')
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none dark:text-slate-200 dark:hover:bg-slate-800 dark:focus-visible:bg-slate-800"
            >
              <User className="h-4 w-4" aria-hidden="true" />
              Edit Profile
            </button>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 focus-visible:bg-red-50 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-950/40 dark:focus-visible:bg-red-950/40"
            >
              {signingOut ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <LogOut className="h-4 w-4" aria-hidden="true" />
              )}
              Sign out
            </button>
          </div>
        )}
      </div>
    </>
  )
}

function ThemeToggle() {
  const { isDark, toggle } = useTheme()
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  )
}

/**
 * The frame every signed-in screen sits in: a fixed rail on the left edge, and everything
 * else filling the rest of the window.
 *
 * Full width, with no centred column. These screens are tables and dashboards -- twelve
 * columns of an invoice list, a chart beside its own legend -- and a max-width wrapper
 * spends the room a wide monitor has on empty margins while the table inside scrolls
 * sideways. Reading measure still matters for prose, so it is the responsibility of the
 * paragraph rather than the shell.
 */
export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="flex min-h-screen">
        {/* `sticky` rather than `fixed`: the rail stays put while the page scrolls without
            the main column needing a left padding that has to be kept in step with it. */}
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-slate-200 bg-white desktop:flex dark:border-slate-800 dark:bg-slate-900">
          <div className="flex h-16 items-center gap-2 border-b border-slate-200 px-4 dark:border-slate-800">
            <Brand />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
            <SidebarNav />
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
            <div className="flex h-16 items-center justify-between gap-4 px-4 tablet:px-6 desktop:px-8">
              {/* The wordmark lives in the rail on desktop, so the bar only repeats it where
                  there is no rail to hold it. */}
              <div className="flex min-w-0 items-center gap-2 desktop:hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMenuOpen(true)}
                  aria-label="Open menu"
                  aria-expanded={menuOpen}
                >
                  <Menu className="h-5 w-5" />
                </Button>
                <Brand compact />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <ThemeToggle />
                <UserMenu />
              </div>
            </div>
          </header>

          <main className="min-w-0 flex-1 px-4 py-6 tablet:px-6 desktop:px-8">
            <Outlet />
          </main>
        </div>
      </div>

      <NavDrawer open={menuOpen} onClose={closeMenu} />
    </div>
  )
}
